import { useEffect, useState, useCallback } from 'react';
import { LoginForm } from '@/components/login-form';
import { AuthenticatedApp } from '@/components/authenticated-app';
import { useAuth } from '@/contexts/auth-context';
import { AuthProvider } from '@/contexts/auth-context';
import { ToastProvider } from '@/contexts/toast-context';
import { ConfirmProvider } from '@/contexts/confirm-context';
import { PWAInstallPrompt } from '@/components/pwa-install-prompt';

// فۆنکشنی شاردنەوەی سپلاش سکرین
function hideSplashScreen() {
  const splash = document.getElementById('splash-screen');
  if (splash) {
    splash.classList.add('hidden');
    setTimeout(() => splash.remove(), 400);
  }
}

// فۆنکشنی پیشاندانی سپلاش سکرین
function showSplashScreen() {
  // ئەگەر سپلاش سکرین نەبوو، دروستی بکە
  let splash = document.getElementById('splash-screen');
  if (!splash) {
    splash = document.createElement('div');
    splash.id = 'splash-screen';
    splash.innerHTML = `
      <div class="splash-logo">ق</div>
      <div class="splash-title">پێشانگای قەندیل</div>
      <div class="splash-subtitle">سیستەمی بەڕێوەبردن</div>
      <div class="splash-loader">
        <div class="splash-dot"></div>
        <div class="splash-dot"></div>
        <div class="splash-dot"></div>
      </div>
    `;
    document.body.insertBefore(splash, document.body.firstChild);
  }
  splash.classList.remove('hidden');
}

// فۆنکشنی نیشاندانی هەڵە لە سپلاش سکرین
function showSplashError(message: string, showRetryButton = false) {
  showSplashScreen();
  const subtitle = document.querySelector('.splash-subtitle');
  const loader = document.querySelector('.splash-loader');
  if (subtitle) {
    subtitle.textContent = message;
    (subtitle as HTMLElement).style.color = '#ef4444';
  }
  if (loader) {
    if (showRetryButton) {
      loader.innerHTML = `
        <button onclick="window.location.reload()" style="
          background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
          color: white;
          border: none;
          padding: 12px 32px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Noto Kufi Arabic', system-ui, sans-serif;
          box-shadow: 0 4px 12px rgba(30, 64, 175, 0.3);
        ">هەوڵدانەوە</button>
      `;
    } else {
      loader.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      `;
    }
    (loader as HTMLElement).style.marginTop = '24px';
  }
}

// فۆنکشنی پشکنینی زیندوو بوونی داتابەیس
async function checkDatabaseHealth(): Promise<boolean> {
  try {
    const apiUrl = import.meta.env.VITE_KV_API_URL;
    if (!apiUrl) return false;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const response = await fetch(`${apiUrl}/health`, {
      method: 'GET',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    console.error('Database health check failed:', error);
    return false;
  }
}

function AppContent() {
  const { isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const [dbHealthy, setDbHealthy] = useState<boolean | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  // گوێگرتن لە گۆڕانی ئینتەرنێت
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // ئەگەر پێشتر ئۆفلاین بوو، دووبارە پشکنین بکە
      if (wasOffline) {
        setDbHealthy(null);
        setRetryCount(0);
        setWasOffline(false);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      showSplashError('هێڵی ئینتەرنێت پەیوەست نییە\nتکایە دووبارە هەوڵ بدەرەوە', true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // پشکنینی ئێستا
    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  // پشکنینی زیندوو بوونی داتابەیس
  useEffect(() => {
    if (!isOnline) return;

    let mounted = true;

    const checkHealth = async () => {
      const healthy = await checkDatabaseHealth();
      if (mounted) {
        setDbHealthy(healthy);
        if (!healthy && retryCount < 3) {
          setTimeout(() => {
            if (mounted) setRetryCount(prev => prev + 1);
          }, 3000);
        }
      }
    };

    checkHealth();

    return () => { mounted = false; };
  }, [retryCount, isOnline]);

  // شاردنەوەی سپلاش سکرین کاتێک هەموو شت ئامادەیە
  useEffect(() => {
    if (!isOnline) {
      return;
    }

    if (dbHealthy === false && retryCount >= 3) {
      console.warn("Backend health check failed, hiding splash screen to proceed with cached data");
      const timer = setTimeout(() => {
        hideSplashScreen();
      }, 200);
      return () => clearTimeout(timer);
    }

    if (dbHealthy === true && !authLoading) {
      const timer = setTimeout(() => {
        hideSplashScreen();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [dbHealthy, authLoading, retryCount, isOnline]);

  // ئەگەر ئۆفلاین بوو
  if (!isOnline) {
    return null;
  }

  if (dbHealthy === null || authLoading || (dbHealthy === false && retryCount < 3)) {
    return null;
  }

  // If dbHealthy check fails after retries, still proceed to render app so IndexedDB cache works
  if (dbHealthy === false) {
    console.warn("Backend health check failed, proceeding with cached data");
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  return <AuthenticatedApp onLogout={logout} />;
}

import { RemindersProvider } from '@/contexts/reminders-context';

function App() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AuthProvider>
          <RemindersProvider>
            <AppContent />
            <PWAInstallPrompt />
          </RemindersProvider>
        </AuthProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}

export default App;
